import { useState, useRef, RefObject } from 'react'

interface UseSmartScrollReturn {
  isUserScrolledUp: boolean
  hasNewContentBelow: boolean
  scrollToBottom: () => void
  forceScrollToBottom: () => void
  handleScroll: () => void
  scrollContainerRef: RefObject<HTMLDivElement>
  contentRef: RefObject<HTMLDivElement>
}

const SCROLL_THRESHOLD = 300
const AUTO_SCROLL_DELAY = 100
const AUTO_SCROLL_FLAG_RESET_DELAY = 500

export const useSmartScroll = (): UseSmartScrollReturn => {
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false)
  const [hasNewContentBelow, setHasNewContentBelow] = useState(false)
  const isUserScrolledUpRef = useRef(false)
  const isAutoScrollingRef = useRef(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new content appears (only if user hasn't scrolled up)
  const scrollToBottom = () => {
    if (isUserScrolledUpRef.current) {
      // User is scrolled up - indicate new content below
      setHasNewContentBelow(true)
      return
    }
    isAutoScrollingRef.current = true
    setTimeout(() => {
      contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      setTimeout(() => {
        isAutoScrollingRef.current = false
      }, AUTO_SCROLL_FLAG_RESET_DELAY)
    }, AUTO_SCROLL_DELAY)
  }

  // Force scroll to bottom (for button click)
  const forceScrollToBottom = () => {
    isUserScrolledUpRef.current = false
    setIsUserScrolledUp(false)
    setHasNewContentBelow(false)
    isAutoScrollingRef.current = true
    setTimeout(() => {
      contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      setTimeout(() => {
        isAutoScrollingRef.current = false
      }, AUTO_SCROLL_FLAG_RESET_DELAY)
    }, AUTO_SCROLL_DELAY)
  }

  // Handle scroll events to detect if user scrolled up
  const handleScroll = () => {
    const container = scrollContainerRef.current
    if (!container) return

    // Ignore scroll events during programmatic scrolling
    if (isAutoScrollingRef.current) return

    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      SCROLL_THRESHOLD

    if (isAtBottom) {
      // User manually scrolled to bottom - hide the button
      isUserScrolledUpRef.current = false
      setIsUserScrolledUp(false)
      setHasNewContentBelow(false)
    } else {
      // User scrolled up - show the button
      isUserScrolledUpRef.current = true
      setIsUserScrolledUp(true)
    }
  }

  return {
    isUserScrolledUp,
    hasNewContentBelow,
    scrollToBottom,
    forceScrollToBottom,
    handleScroll,
    scrollContainerRef,
    contentRef
  }
}
